import { Site, SLD, type HeaderService, type ListItem } from '@patsnap-utils/biz';
import { getCurrentLanguage } from '@eureka/language-tool';
import '@patsnap-ui/icon/assets/solid/HistoryRecord.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/AI.svg';
import '@patsnap-ui/icon/assets/solid/Gift.svg';
import '@patsnap-ui/icon/assets/solid/credits.svg';
import { combineHash, combineUrl } from '@eureka/utils/url';
import { clientDeferred } from '@eureka/utils/client';
import { isCNRegion } from '@eureka/utils/environment';
import { accountDeferred } from '@eureka/utils/account';
import { controlDeferred } from '@eureka/utils/control';
import { rechargeInfoDeferred } from '@eureka/utils/recharge-info';

type ILocale = 'cn' | 'en' | 'tw' | 'jp'
export interface CustomLinksOptions {
    checkAgentSettingsPermission?: () => Promise<boolean>;
}

export async function customProductsLinkInUserCenter(headerService: HeaderService, options?: CustomLinksOptions) {
    const locale = getCurrentLanguage() as ILocale;
    const buildin = headerService.userCenterService.beforeRenderMenuItems

    const [{ isFeiShu }, account] = await Promise.all([
        clientDeferred.promise,
        accountDeferred.promise
    ])
    let isPro = false;
    if (headerService.userCenterService.logged) {
        const { has_pay_packages } = await rechargeInfoDeferred.promise
        isPro = !!has_pay_packages
    }

    const isPersonal = !account?._info?.company_id
    const { credit_package, count_package } = await controlDeferred.promise

    // 异步检查agent-settings权限
    let hasAgentSettingsPermission = false;
    if (options?.checkAgentSettingsPermission) {
        options.checkAgentSettingsPermission().then(permission => {
            hasAgentSettingsPermission = permission;
        }).catch(() => {
            hasAgentSettingsPermission = false;
        });
    }

    headerService.userCenterService.beforeRenderMenuItems = function beforeRenderMenuItems(items: ListItem[]) {
        const _items = buildin(items);
        _items.splice(1, 0, {
            href: new Site({ sld: SLD.EUREKA, pathname: 'history' }).href,
            label: ({
                cn: '历史记录',
                en: 'History',
                jp: '履歴',
                tw: '歷史記錄',
            })[locale],
            icon: 'SolidHistoryRecord',
            key: 'history',
        })

        // 只在有权限时添加个性化设置菜单
        if (hasAgentSettingsPermission && !isFeiShu) {
            _items.splice(2, 0, {
                href: combineHash(combineUrl(location.origin, 'ls'), 'user-setting/agent-settings'),
                label: ({
                    cn: '个性化设置',
                    en: 'customization',
                    jp: 'エージェント設定',
                    tw: '個性化設置',
                })[locale],
                icon: 'SolidAi',
                key: 'agent-settings',
            })
        }

        /**
         * 非飞书 && 非海外个人
         */
        if (!isFeiShu && !(isPersonal && !isCNRegion())) {
            const versionUrl = new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package' }).href;

            _items.splice(0, 0, {
                href: versionUrl,
                label: ({
                    cn: '所有版本',
                    en: 'All versions',
                    jp: 'すべてのバージョンになります',
                    tw: '所有版本',
                })[locale],
                icon: 'SolidRocket',
                key: 'version',
            })
        }

        if (count_package) {
            _items.splice(0, 0, {
                href: combineHash(combineUrl(location.origin, 'ls'), 'user-setting/amount'),
                label: ({
                    cn: '用量',
                    en: 'Usage',
                    jp: '利用回数',
                    tw: '用量',
                })[locale],
                icon: 'Userpackage',
                key: 'package',
            })
        }

        if (credit_package) {
            _items.splice(0, 0, {
                href: new Site({ sld: SLD.EUREKA, pathname: 'credits' }).href,
                label: ({
                    cn: '积分',
                    en: 'Credits',
                    jp: 'クレジット',
                    tw: '積分',
                })[locale],
                icon: 'SolidCredits',
                key: 'credits',
            })
        }

        if (isFeiShu) {
            _items.splice(-2, 2)
        }

        if (isPersonal && isPro) {
            // 优化添加管理订阅菜单项逻辑
            const manageSubItem = {
                key: 'manageSubscription',
                label: ({
                    cn: '管理订阅',
                    en: 'Manage Subscription',
                    jp: 'サブスクリプション管理',
                    tw: '管理訂閱',
                })[locale],
                href: `${import.meta.env.VITE_EUREKA_URI}/user-setting/package/management`,
                icon: 'SolidRocket',
            };
            const creditsItemIndex = _items.findIndex(item => item.key === 'credits');
            // 如果积分菜单项存在，则插入到积分菜单项后，否则插到最前面
            if (creditsItemIndex !== -1) {
                _items.splice(creditsItemIndex + 1, 0, manageSubItem);
            } else {
                _items.unshift(manageSubItem);
            }
        }

        if (isPersonal) {
            const nonePackages = ['package', 'settings', 'team']
            return _items.filter(item => !nonePackages.includes(item.key as string));
        }

        return _items;
    }
}
